import * as React from 'react'

import { BordersStyles, Box, Table, Text } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  BreakConfigurationFragment,
  TimesheetEstimatedTimeFragment,
  TimesheetPageEmployeesSettingFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import type moment from 'moment'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import { minutesToText } from 'modules/core/lib/duration'

import Leaves from 'modules/attendance/components/ClockIn/Leaves'
import { BreaksColumn } from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/BreaksColumn'
import ComplementaryHoursColumn from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ComplementaryHoursColumn'
import DayColumn from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/DayColumn'
import DurationColumn from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/DurationColumn'
import ExtraHoursColumn from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ExtraHoursColumn'
import OptionsColumn from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/OptionsColumn'
import { type GroupedByDateWorkedBreaks } from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/types'
import type Store from 'modules/attendance/components/ClockIn/Store'
import { AttendanceShiftLocationNodes } from 'modules/attendance/hooks/shift_locations/useFetchShiftLocations'
import { Inconsistencies } from 'modules/attendance/hooks/useShiftInconsistencies'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { ValidationError } from 'modules/attendance/lib/validateShiftBreakDurationRules'
import type CalendarDay from 'modules/attendance/store/models/CalendarDay'

interface RowTabProps {
  store: Store
  open: boolean
  dateKey: string
  className: string
  date: moment.Moment
  isBeforeToday: boolean
  hasFutureDate: boolean
  showHourBalance: boolean
  calendarDay: CalendarDay
  hasUnresolvedShift: boolean
  balanceMinutes: number
  inconsistencies?: Inconsistencies
  toggle: (value: boolean) => () => void
  workedBreaks: GroupedByDateWorkedBreaks
  borderLeftStyleDirection: BordersStyles
  restTimeInconsistencies?: ValidationError[]
  shiftLocations?: AttendanceShiftLocationNodes
  attendanceSetting: TimesheetPageEmployeesSettingFragment
  estimatedTime: TimesheetEstimatedTimeFragment
  trackedMinutes: number
  workedMinutes: number
  effectiveWorkedMinutes: number
  workedMinutesNotApproved: number
  breaksMinutesRounded: number
  hasCorrectiveBreaks: boolean
  hasExtraHourManagementV2: boolean
  showExtraHoursColumn: boolean
  showComplementaryHoursColumn: boolean
  allBreaks: BreakConfigurationFragment[]
  shifts: TimesheetPageShiftFragment[]
  setBackground: (
    calendarDay: CalendarDay,
    isTab?: boolean
  ) => 'champagne100' | 'grey100' | 'grey000White'
}

const RowTab = ({
  open,
  date,
  store,
  toggle,
  dateKey,
  className,
  calendarDay,
  workedBreaks,
  setBackground,
  hasFutureDate,
  isBeforeToday,
  shiftLocations,
  showHourBalance,
  hasUnresolvedShift,
  inconsistencies,
  restTimeInconsistencies,
  borderLeftStyleDirection,
  attendanceSetting,
  estimatedTime,
  balanceMinutes,
  trackedMinutes,
  workedMinutes,
  effectiveWorkedMinutes,
  breaksMinutesRounded,
  hasCorrectiveBreaks,
  hasExtraHourManagementV2,
  showExtraHoursColumn,
  showComplementaryHoursColumn,
  workedMinutesNotApproved,
  allBreaks,
  shifts,
}: RowTabProps) => {
  const { aggregatedWorkedTime, balances } = useTimesheetData()
  const onClick = (e: React.BaseSyntheticEvent) => {
    e.stopPropagation()

    if (e.target.tagName === 'TD' && !hasFutureDate) {
      toggle(!open)()
      track(trackingEvents.timeTracking.expandOrCollapseDayRow, {
        action: !open ? 'expand' : 'collapse',
      })
    }
  }

  const { featureAvailability: hasTimeTrackingInconsistencies } = useAttendanceFeatures(
    Features.time_tracking_time_alerts
  )

  const backgroundColor = setBackground(calendarDay, true)
  const isToday = !isBeforeToday && !hasFutureDate
  const balanceAmount =
    showHourBalance && isBeforeToday && !!balanceMinutes
      ? minutesToText(balanceMinutes, false, true, true)
      : undefined

  // Calculate extra hours data for the specific date
  const dateString = i18nDateToString(date, 'backend')
  const dayWorkedTimeBlocks =
    aggregatedWorkedTime?.workedTimeBlocks?.filter((block) => block.date === dateString) || []

  // Get balance data for this specific date
  const dayBalance = balances.find((balance) => balance.date === dateString)

  const extraHoursData = {
    extraHoursMinutes: dayWorkedTimeBlocks
      .filter((block) => block.extraHour && block.approved)
      .reduce((sum, block) => sum + (block.minutes || 0), 0),
    contractHoursMinutes: estimatedTime?.contractMinutes || 0,
    plannedHoursMinutes: estimatedTime?.minutes || 0,
    workedHoursMinutes: dayWorkedTimeBlocks.reduce((sum, block) => sum + (block.minutes || 0), 0),
    effectiveWorkedMinutes,
  }

  const complementaryHoursData = {
    complementaryHoursMinutes: dayBalance?.complementaryTimeMinutes ?? 0,
    contractHoursMinutes: estimatedTime?.contractMinutes || 0,
    plannedHoursMinutes: estimatedTime?.minutes || 0,
    workedHoursMinutes: dayWorkedTimeBlocks.reduce((sum, block) => sum + (block.minutes || 0), 0),
    effectiveWorkedMinutes,
  }

  return (
    <Table.Row
      onClick={onClick}
      clickable
      link
      backgroundHover={isToday ? 'champagne100' : 'grey100'}
      background={backgroundColor}
      className={className}
      data-intercom-target="attendance-row"
    >
      <Table.Cell padding="s0">
        <Box border={borderLeftStyleDirection} paddingX="s24" paddingY="s16" width="fit-content">
          <DayColumn date={date} calendarDay={calendarDay} />
        </Box>
      </Table.Cell>
      <Table.Cell paddingX="s0">
        <DurationColumn
          date={date}
          store={store}
          calendarDay={calendarDay}
          hasUnresolvedShift={hasUnresolvedShift}
          workedBreaks={workedBreaks[dateKey] ?? []}
          balanceAmount={balanceAmount}
          trackedMinutes={trackedMinutes}
          workedMinutes={workedMinutes}
          workedMinutesNotApproved={workedMinutesNotApproved}
          breaksMinutesRounded={breaksMinutesRounded}
          hasCorrectiveBreaks={hasCorrectiveBreaks}
          hasExtraHourManagementV2={hasExtraHourManagementV2}
          attendanceSetting={attendanceSetting}
          estimatedTime={estimatedTime}
          hasTimeTrackingInconsistencies={hasTimeTrackingInconsistencies}
        />
      </Table.Cell>
      {showHourBalance && hasExtraHourManagementV2 && (
        <Table.Cell>
          <Box width="fit-content">
            {!!balanceAmount && (
              <Text size="300" weight="medium" color="grey800">
                {balanceAmount}
              </Text>
            )}
          </Box>
        </Table.Cell>
      )}
      {hasCorrectiveBreaks && (
        <Table.Cell>
          <BreaksColumn
            breaksMinutesRounded={breaksMinutesRounded}
            calendarDay={calendarDay}
            allBreaks={allBreaks}
            shifts={shifts}
          />
        </Table.Cell>
      )}
      {showHourBalance && showExtraHoursColumn && !showComplementaryHoursColumn && (
        <Table.Cell>
          {!hasFutureDate && (
            <ExtraHoursColumn
              extraHoursData={extraHoursData}
              grayedOutColor="grey800"
              extraHoursBaseline={attendanceSetting.extraHoursBaseline}
            />
          )}
        </Table.Cell>
      )}
      {showHourBalance && showComplementaryHoursColumn && (
        <Table.Cell>
          {!hasFutureDate && (
            <ComplementaryHoursColumn
              complementaryHoursData={complementaryHoursData}
              grayedOutColor="grey800"
              complementaryHoursBaseline={
                attendanceSetting.complementaryHoursBaseline ?? 'contract_hours'
              }
            />
          )}
        </Table.Cell>
      )}
      <Table.Cell paddingX="s0" width={200}>
        <Leaves leaves={calendarDay.leaves} />
      </Table.Cell>
      <Table.Cell paddingX="s0">
        <OptionsColumn
          date={date}
          store={store}
          open={open}
          toggle={toggle}
          hasFutureDate={hasFutureDate}
          shiftLocations={shiftLocations}
          inconsistencies={inconsistencies}
          attendanceSetting={attendanceSetting}
          restTimeInconsistencies={restTimeInconsistencies}
          shifts={shifts}
        />
      </Table.Cell>
    </Table.Row>
  )
}

export default RowTab
